import React, { useEffect, useState } from 'react';
import { Button, Modal, Table, Select, Space, message } from 'antd';
import userService from '../../services/user';
import numberToPrice from '../../helpers/numberToPrice';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

export default function UserTransactionsModal({ uuid, visible, handleCancel }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
  });
  const [filters, setFilters] = useState({
    status: undefined,
    payable_type: undefined,
  });
  const { t } = useTranslation();

  function fetchTransactions(page = 1) {
    setLoading(true);
    const params = {
      page,
      perPage: pagination.pageSize,
      ...filters,
    };
    userService
      .getUserTransactions(uuid, params)
      .then((res) => {
        setData(res.data);
        setPagination({
          current: res.meta.current_page,
          pageSize: res.meta.per_page,
          total: res.meta.total,
        });
      })
      .catch((err) => {
        message.error(err.response?.data?.message || 'Failed to fetch transactions');
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (visible && uuid) {
      fetchTransactions();
    }
  }, [visible, uuid, filters]);

  const handleTableChange = (pagination) => {
    fetchTransactions(pagination.current);
  };

  const handleStatusFilter = (value) => {
    setFilters({ ...filters, status: value });
  };

  const handleTypeFilter = (value) => {
    setFilters({ ...filters, payable_type: value });
  };

  const columns = [
    {
      title: t('transaction.id'),
      dataIndex: 'givzo_ref_no',
      key: 'givzo_ref_no',
    },
    {
      title: t('order.id'),
      dataIndex: 'givzo_order_id',
      key: 'givzo_order_id',
    },
    {
      title: t('type'),
      dataIndex: 'payable_type',
      key: 'payable_type',
    },
    {
      title: t('product.name'),
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: t('amount'),
      dataIndex: 'price',
      key: 'price',
      render: (price) => numberToPrice(price / 100, '₹'),
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const colorMap = {
          SUCCESS: 'green',
          FAILED: 'red',
          PENDING: 'orange',
          REFUNDED: 'blue',
        };
        return <span style={{ color: colorMap[status] || 'default' }}>{status}</span>;
      },
    },
    {
      title: t('date'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
  ];

  return (
    <Modal
      visible={visible}
      title={t('transaction.history')}
      onCancel={handleCancel}
      footer={[
        <Button key="back" onClick={handleCancel}>
          {t('close')}
        </Button>,
      ]}
      width={1000}
    >
      <Space style={{ marginBottom: 16 }}>
        <Select
          placeholder={t('filter.by.status')}
          style={{ width: 150 }}
          allowClear
          onChange={handleStatusFilter}
          value={filters.status}
        >
          <Option value="SUCCESS">{t('success')}</Option>
          <Option value="FAILED">{t('failed')}</Option>
          <Option value="PENDING">{t('pending')}</Option>
          <Option value="REFUNDED">{t('refunded')}</Option>
        </Select>
        <Select
          placeholder={t('filter.by.type')}
          style={{ width: 200 }}
          allowClear
          onChange={handleTypeFilter}
          value={filters.payable_type}
        >
          <Option value="Giftcard">{t('gift.card')}</Option>
          <Option value="Prepaid">{t('prepaid')}</Option>
          <Option value="Postpaid">{t('postpaid')}</Option>
          <Option value="Electricity">{t('electricity')}</Option>
          <Option value="Gas">{t('gas')}</Option>
          <Option value="DTH">{t('dth')}</Option>
        </Select>
      </Space>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey="id"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: false,
        }}
        onChange={handleTableChange}
      />
    </Modal>
  );
}
